import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { mdiMagnify, mdiPlus, mdiPencil, mdiTrashCan } from '@mdi/js';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Skeleton from '@/components/layout/Skeleton';
import AlertBox from '@/components/layout/AlertBox';
import Modal from '@/components/layout/Modal';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import { useDebounce } from '../chat/useDebounce';
import FormMcpServidor from './FormMcpServidor';
import { McpServidor, TIMEOUT_MCP_PADRAO } from '@tera/shared/src/types/McpServidor';
import styles from './page.module.css';
import Badge from '@/components/layout/Badge';

const NOVO: McpServidor = {
    nome: '',
    descricao: '',
    url: '',
    auth_tipo: 'none',
    ferramentas_permitidas: [],
    timeout_ms: TIMEOUT_MCP_PADRAO,
    ativo: true,
};

const Page = () => {
    const [search, setSearch] = React.useState('');
    const [selecionado, setSelecionado] = React.useState<McpServidor | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [mostrarForm, setMostrarForm] = React.useState(false);
    const [aExcluir, setAExcluir] = React.useState<number | null>(null);

    const buscaDebounce = useDebounce(search, 500);

    const query = useQuery(['mcp_servidor:search', buscaDebounce], async () => {
        const res = await axios.get('/api/mcp_servidor', {
            params: { txt: buscaDebounce || undefined, t: 50 },
        });
        return res.data;
    });

    const salvarMutation = useMutation(
        async (servidor: McpServidor) => {
            setFieldErrors({});
            return servidor.id
                ? axios.put(`/api/mcp_servidor/${servidor.id}`, servidor)
                : axios.post('/api/mcp_servidor', servidor);
        },
        {
            onSuccess: () => {
                toast('Servidor salvo com sucesso', { type: 'success', position: 'top-center' });
                query.refetch();
                setMostrarForm(false);
                setSelecionado(null);
            },
            onError: (error: any) => {
                setFieldErrors(error.response?.data?.fieldErrors || {});
                toast(error.response?.data?.msg ?? 'Erro ao salvar servidor', {
                    type: 'error',
                    position: 'top-center',
                    autoClose: 8000,
                });
            },
        },
    );

    const excluirMutation = useMutation(
        async (id: number) => axios.delete(`/api/mcp_servidor/${id}`),
        {
            onSuccess: () => {
                toast('Servidor excluído', { type: 'success', position: 'top-center' });
                query.refetch();
                setAExcluir(null);
            },
            onError: (error: any) =>
                toast(error.response?.data?.msg ?? 'Erro ao excluir servidor', {
                    type: 'error',
                    position: 'top-center',
                }),
        },
    );

    const abrirForm = (servidor: McpServidor | null) => {
        setSelecionado(servidor ?? { ...NOVO });
        setMostrarForm(true);
    };

    const renderConteudo = () => {
        if (query.isLoading)
            return (
                <VBox gap={2}>
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                </VBox>
            );

        if (query.error)
            return (
                <AlertBox variant="danger">
                    <p>
                        Erro ao carregar servidores MCP:{' '}
                        {(query.error as any)?.message || 'Erro desconhecido'}
                    </p>
                </AlertBox>
            );

        const cabecalhos: DataTableHeader[] = [
            { label: 'Nome', width: '25%', render: (linha: any) => <HBox>{linha.nome}{' '}{linha.ativo && <Badge variant='info'>Ativo</Badge>}</HBox> },
            { label: 'URL', width: '50%', column: 'url' },
            {
                label: 'Ferramentas',
                width: '25%',
                render: (linha: any) => (
                    <span>
                        {linha.ferramentas_permitidas?.length
                            ? `${linha.ferramentas_permitidas.length} liberada(s)`
                            : 'Todas'}
                    </span>
                ),
            },
            {
                label: 'Ações',
                width: '120px',
                render: (linha: any) => (
                    <HBox gap={8}>
                        <Botao
                            variant="none-primary"
                            title="Editar"
                            icon={mdiPencil}
                            onClick={() => abrirForm(linha)}
                        />
                        <Botao
                            variant="none-danger"
                            title="Excluir"
                            icon={mdiTrashCan}
                            onClick={() => setAExcluir(linha.id)}
                        />
                    </HBox>
                ),
            },
        ];

        return <DataTable cabecalhos={cabecalhos} linhas={query.data?.result ?? []} />;
    };

    return (
        <LayoutUsuario>
            <div className={styles.container}>
                <VBox gap={24}>
                    <HBox>
                        <div className={styles.headerTitulo}>
                            <Titulo>Servidores MCP</Titulo>
                            <Descricao>
                                Sistemas externos que publicam as próprias ferramentas pelo Model
                                Context Protocol. Ao liberar um servidor para um agente de IA, ele
                                passa a poder usar essas ferramentas sem precisar de código novo.
                            </Descricao>
                        </div>
                        <div className={styles.headerAcoes}>
                            <Botao
                                variant="primary"
                                icon={mdiPlus}
                                title="Novo servidor"
                                onClick={() => abrirForm(null)}
                            >
                                Novo servidor
                            </Botao>
                        </div>
                    </HBox>

                    <CampoTexto
                        nome="search"
                        rotulo=""
                        placeholder="Buscar servidor MCP..."
                        valor={search}
                        onChange={setSearch}
                        icone={mdiMagnify}
                    />

                    <div>{renderConteudo()}</div>
                </VBox>
            </div>

            <Modal
                instanceId="mcp-servidor-form-modal"
                show={mostrarForm}
                size="lg"
                title={selecionado?.id ? 'Editar servidor MCP' : 'Novo servidor MCP'}
                onClose={() => setMostrarForm(false)}
                onCancel={() => setMostrarForm(false)}
                onConfirm={() => selecionado && salvarMutation.mutate(selecionado)}
                confirmProps={{
                    label: 'Salvar',
                    variant: 'primary',
                    disabled: salvarMutation.isLoading,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <FormMcpServidor
                    // Remonta ao trocar de servidor, para o resultado do teste de
                    // conexão não vazar de um cadastro para o outro.
                    key={selecionado?.id ?? 'novo'}
                    value={selecionado}
                    onChange={setSelecionado}
                    fieldErrors={fieldErrors}
                />
            </Modal>

            <Modal
                instanceId="mcp-servidor-delete-modal"
                show={!!aExcluir}
                size="sm"
                title="Confirmar exclusão"
                onClose={() => setAExcluir(null)}
                onCancel={() => setAExcluir(null)}
                onConfirm={() => aExcluir && excluirMutation.mutate(aExcluir)}
                confirmProps={{
                    label: 'Confirmar',
                    variant: 'danger',
                    disabled: excluirMutation.isLoading,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <p>Tem certeza que deseja excluir este servidor MCP?</p>
                <p>Os agentes que o usam perderão as ferramentas dele imediatamente.</p>
            </Modal>
        </LayoutUsuario>
    );
};

export default Page;
